'use client';

import InfoTooltip from '../components/InfoTooltip';
import { Label } from '../typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { tv } from 'tailwind-variants';

import { faArrowDown, faArrowUp } from '@icons/classic/regular';

import type { SortingState, TableState, Updater } from '@tanstack/react-table';

interface BaseTableHeaderProps<L, A> {
  /** The sort slice of the table state; `table.getState()` satisfies it. */
  tableState: Pick<TableState, 'sorting'>;
  label: L;
  accessor: A;
  className?: string;
  sortable?: boolean;
  infoTooltip?: string;
}

interface NonSortableTableHeaderProps<L, A> extends BaseTableHeaderProps<L, A> {
  setSorting?: never;
}

interface SortableTableHeaderProps<L, A> extends BaseTableHeaderProps<L, A> {
  setSorting: (updater: Updater<SortingState>) => void;
  sortable: true;
}

type TableHeaderProps<L, A> = NonSortableTableHeaderProps<L, A> | SortableTableHeaderProps<L, A>;

const tableHeader = tv({
  base: 'gap-1 inline-flex items-center',
  variants: {
    isSortable: {
      true: 'cursor-pointer [&_*]:cursor-pointer',
      false: '',
    },
  },
});

/**
 * One step of a column's sort cycle: descending, then ascending, then unsorted.
 * A column that is not the sorted one starts the cycle at descending.
 */
function nextSortingState(accessor: string, sorting: SortingState | undefined): SortingState {
  const [active] = sorting ?? [];
  if (active?.id !== accessor) return [{ id: accessor, desc: true }];
  return active.desc ? [{ id: accessor, desc: false }] : [];
}

const TableColumnHeader = <L extends string, A extends string>({
  tableState,
  setSorting,
  sortable,
  label,
  accessor,
  className,
  infoTooltip,
}: TableHeaderProps<L, A>) => {
  const { sorting } = tableState;
  const { id: sortingId, desc } = sorting?.[0] ?? {};
  const isSorting = accessor === sortingId;
  const isSortable = sortable && setSorting;

  if (!label) return null;

  return (
    <div
      className={tableHeader({ className, isSortable: !!isSortable })}
      onClick={() => isSortable && setSorting(nextSortingState(accessor, sorting))}
    >
      <Label role={isSortable ? 'button' : undefined} size="sm" weight="medium">
        {label}
      </Label>
      {isSorting && <FontAwesomeIcon icon={desc ? faArrowDown : faArrowUp} size="sm" />}
      {infoTooltip && <InfoTooltip size="sm" tooltip={infoTooltip} />}
    </div>
  );
};

export default TableColumnHeader;
